import * as Yup from 'yup';
import { AppRegistrationSection } from '../../AppRegistrationSection';
import { ButtonActor, InputActor, Text, Title } from 'components';
import { Frame } from 'components/organisms';
import { frameConfig } from '../../AppConfig';
import { getScreenTranslation } from '../../utils/translations';
import { mobilePhoneNumberValid } from '../../utils/regularExpressions';
import { phoneSanitizer } from '../../utils/sanitizers';
import { useErrorManager } from 'InternalLibrary';
import { useFormik } from 'formik';
import { usePhoneVerificationScreenLogic } from 'pages/PhoneVerification/usePhoneVerificationScreenLogic';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { WarningGreyIcon } from 'components/icons';
import classNames from 'classnames';
import layout from 'scss/layout.module.scss';
import styles from './IncorrectCode.module.scss';

export default function IncorrectCode() {
  const [loading, setLoading] = useState(false);
  const { error, setError: setErrorManager } = useErrorManager();
  const i18next = useTranslation();
  const { updateClientPhone } = usePhoneVerificationScreenLogic();

  const validationSchema = Yup.object().shape({
    phoneNumber: Yup.string()
      .required(i18next.t('validation:required_field'))
      .test('wrong_format', i18next.t('validation:wrong_format'), (value) =>
        mobilePhoneNumberValid(value ?? ''),
      ),
  });

  if (error) {
    setErrorManager('web_code_resend_attempts_exceeded');
  }

  const onSubmit = async (data: { phoneNumber: string }) => {
    formik.setStatus({ errors: {} });
    setLoading(true);
    try {
      await updateClientPhone(data.phoneNumber);
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      phoneNumber: '',
    },
    validationSchema,
    onSubmit,
  });

  const content = getScreenTranslation('incorrectCode', i18next);

  return (
    <Frame
      {...frameConfig}
      bodyWrapperClassName={layout['frame-x-centered']}
      sectionID={AppRegistrationSection.Identification}
    >
      <Title icon={<WarningGreyIcon />} text={content.heading} />
      <Text
        text={content.subheading}
        level={2}
        kind="p"
        className={styles.subheading}
        dataTest="subheading"
      />
      <form
        onSubmit={formik.handleSubmit}
        className={classNames(layout['width-sm'], styles.form)}
      >
        <InputActor
          formik={formik}
          name="phoneNumber"
          label={content.mobile_phone}
          sanitizer={phoneSanitizer}
          analytics={{
            dontTrackContent: true,
          }}
          inputProps={{
            allowSpaces: false,
          }}
          fieldGroupProps={{
            dataTestValidationText: 'validationText',
          }}
        />

        <ButtonActor
          buttonProps={{
            className: styles['continue-button'],
            dataTest: 'subheading',
            type: 'submit',
            loading,
          }}
          formik={formik}
        >
          {content.continue_button}
        </ButtonActor>
      </form>
    </Frame>
  );
}
